import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api, type Me } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';
import { useCompany } from './catalog';
import { EndingAgreements } from './agreements';
import { MonthSummary } from './reports';
import { UpcomingTaxes } from './taxes';

interface Dashboard {
  totals: { issuedThisMonth: number; issuedCount: number; receivable: number; overdue: number; overdueCount: number; receivedThisMonth: number; payable: number; drafts: number };
  recent: { id: number; status: string; series: string; number: string; issueDate: string; total: number; currency: string; buyerName: string; overdue: boolean }[];
}

const STATUS: Record<string, string> = { draft: 'Juodraštis', sent: 'Išsiųsta', paid: 'Apmokėta', cancelled: 'Atšaukta' };

export function DashboardPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const data = useQuery({ queryKey: ['dashboard'], queryFn: () => api.get<Dashboard>('api/v1/dashboard') });
  const company = useCompany();
  const logoVersion = company.dataUpdatedAt;
  const s = data.data?.totals;
  const month = new Date().toLocaleDateString(undefined, { month: 'long' });
  return (
    <PanelShell me={me} head={<PageHead eyebrow={me.company.name} {...(me.company.hasLogo ? { logo: `api/v1/company/logo?v=${logoVersion}` } : {})} title={t('Laba diena, {name}', { name: me.user.name.split(' ')[0] ?? me.user.name })} text={t('Štai jūsų sąskaitų apžvalga.')}
      action={<Link className="button button--primary" to="/outgoing/new">+ {t('Nauja sąskaita')}</Link>} />}>
      {data.isError ? <Alert kind="error">{errorText(t, data.error)}</Alert> : null}
      <section className="stat-grid">
        <article><span>{t('Išrašyta šį mėnesį')}</span><strong>{money(s?.issuedThisMonth ?? 0)}</strong><small>{s?.issuedCount ?? 0} {t('sąsk.')} · {month}</small></article>
        <article><span>{t('Laukiama apmokėjimų')}</span><strong>{money(s?.receivable ?? 0)}</strong><small>{s?.drafts ? t('{n} juodraščiai', { n: s.drafts }) : t('Išsiųstos, dar neapmokėtos')}</small></article>
        <article className={s?.overdueCount ? 'stat--alert' : undefined}><span>{t('Vėluoja')}</span><strong>{money(s?.overdue ?? 0)}</strong><small>{s?.overdueCount ?? 0} {t('sąsk.')} · <Link to="/invoices" search={{ tab: 'out', view: 'unpaid' }}>{t('Neapmokėtos')} →</Link></small></article>
        <article><span>{t('Gauta šį mėnesį')}</span><strong>{money(s?.receivedThisMonth ?? 0)}</strong><small>{t('Neapmokėta')}: {money(s?.payable ?? 0)}</small></article>
      </section>
      {me.disabledModules?.includes('/taxes') || me.subscription?.locked.includes('/taxes') ? null : <UpcomingTaxes />}
      {me.disabledModules?.includes('/agreements') || me.subscription?.locked.includes('/agreements') ? null : <EndingAgreements />}
      <MonthSummary vatPayer={company.data?.isVatPayer ?? false} />
      <section className="panel-card">
        <div className="card-head"><div><p className="eyebrow">{t('Naujausios')}</p><h2>{t('Sąskaitos')}</h2></div><Link to="/invoices" search={{ tab: 'out', view: 'all' }}>{t('Visos sąskaitos')} →</Link></div>
        <div className="table-wrap"><table className="invoice-table">
          <thead><tr><th>{t('Nr.')}</th><th>{t('Klientas')}</th><th>{t('Data')}</th><th>{t('Būsena')}</th><th className="right">{t('Suma')}</th></tr></thead>
          <tbody>
            {data.isSuccess && !data.data.recent.length ? <tr><td colSpan={5} className="empty">{t('Sąskaitų dar nėra.')} <Link to="/outgoing/new">{t('Sukurkite pirmąją')}</Link></td></tr> : null}
            {data.data?.recent.map((r) => (
              <tr key={r.id}>
                <td><Link to="/outgoing/$id" params={{ id: String(r.id) }}><strong>{`${r.series} ${r.number}`.trim()}</strong></Link></td>
                <td>{r.buyerName}</td><td>{r.issueDate}</td>
                <td><span className={`badge badge--${r.overdue ? 'overdue' : r.status}`}>{t(r.overdue ? 'Vėluoja' : STATUS[r.status] ?? r.status)}</span></td>
                <td className="right"><strong>{money(r.total, r.currency)}</strong></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </section>
    </PanelShell>
  );
}

